import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/site/Motion";
import { ReviewForm } from "../components/site/ReviewForm";
import { http, apiError, DIVISIONS } from "../lib/api";

const OptOut = ({ token, prompt }) => {
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const go = async () => {
    setState("busy");
    try {
      await http.post(`/review-request/${token}/opt-out`);
      setState("done");
    } catch (e) {
      setErr(apiError(e));
      setState("idle");
    }
  };
  if (state === "done") return <p className="text-sm text-slate-200 flex items-center gap-2" data-testid="review-optout-done"><CheckCircle2 className="w-4 h-4 text-gold" /> You won't receive any more review request emails from us.</p>;
  return (
    <div className={prompt ? "border hairline p-8 text-center space-y-4" : "text-xs text-slate-400"} data-testid="review-optout">
      {prompt && <p className="font-display text-2xl">Stop review request emails?</p>}
      {prompt ? (
        <button className="btn-gold" onClick={go} disabled={state === "busy"} data-testid="review-optout-confirm">{state === "busy" && <Loader2 className="w-4 h-4 animate-spin" />} Yes, stop these emails</button>
      ) : (
        <>Prefer not to receive review request emails? <button className="underline hover:text-gold" onClick={go} disabled={state === "busy"} data-testid="review-optout-link">Opt out</button></>
      )}
      {err && <p className="text-xs text-rose-300 mt-2">{err}</p>}
    </div>
  );
};

export default function WriteReview() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const optout = params.get("optout") === "1";
  const { data, error, isLoading } = useQuery({ queryKey: ["review-request", token], queryFn: async () => (await http.get(`/review-request/${token}`)).data, retry: false, enabled: !optout });
  const div = DIVISIONS.find((d) => d.key === data?.division);
  const body = optout ? <OptOut token={token} prompt /> : isLoading ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : error ? (
    <div className="border hairline p-10 text-center" data-testid="write-review-invalid">
      <p className="font-display text-3xl">This link can't be used.</p>
      <p className="text-sm text-slate-300 mt-3">{apiError(error)}</p>
      <Link to="/contact" className="btn-ghost mt-6 inline-flex" data-testid="write-review-contact-link">Contact us</Link>
    </div>
  ) : (
    <>
      {data?.ref && <p className="text-xs font-mono text-slate-400 mb-6" data-testid="write-review-ref">Inquiry {data.ref}{div ? ` · ${div.name}` : ""}</p>}
      <ReviewForm token={token} prefill={data} />
      <p className="text-xs text-slate-400 mt-6">Reviews are read by our team and published only after approval. Your email address is never shown.</p>
      <div className="mt-3"><OptOut token={token} /></div>
    </>
  );
  return (
    <div data-testid="write-review-page">
      <PageHero eyebrow="Share your experience" title={["Thank you for", "working with us."]} intro={div ? `Tell us how your experience with our ${div.name} went.` : "Tell us how your experience with Selby Asset Group went."} testid="write-review-hero" />
      <section className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-20">{body}</section>
    </div>
  );
}
